import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import { CalendarBlank, LockSimple, LockSimpleOpen, ArrowsClockwise, CheckCircle, WarningCircle, XCircle, ListChecks, Flag } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS_TONE = {
  open: "bg-emerald-50 text-emerald-700 border-emerald-200",
  soft_closed: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-700 border-slate-300",
  locked: "bg-rose-50 text-rose-700 border-rose-200",
};

const ACTIONS_BY_STATUS = {
  open: [{ action: "soft_close", label: "Soft close" }, { action: "close", label: "Close" }],
  soft_closed: [{ action: "close", label: "Close" }, { action: "reopen", label: "Reopen" }],
  closed: [{ action: "lock", label: "Lock" }, { action: "reopen", label: "Reopen" }],
  locked: [],
};

const CHECK_ICON = {
  pass: <CheckCircle size={14} weight="fill" className="text-emerald-600" />,
  warn: <WarningCircle size={14} weight="fill" className="text-amber-500" />,
  fail: <XCircle size={14} weight="fill" className="text-rose-600" />,
};

function ChecklistDialog({ period, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!period) return;
    setData(null);
    api.get(`/finance/gl/periods/${period}/close-checklist`)
      .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, [period]);
  return (
    <Dialog open={!!period} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Close checklist · {period}</DialogTitle>
          <DialogDescription>All FAIL items must clear before this period can be closed. Warnings are advisory.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Checking…</div>
        ) : (
          <div className="space-y-2" data-testid="close-checklist-items">
            {data.checks.map((c) => (
              <div key={c.id} className="flex items-start gap-2 rounded-md border border-slate-200 p-2.5" data-testid={`check-${c.id}`}>
                <div className="mt-0.5">{CHECK_ICON[c.status]}</div>
                <div>
                  <div className="text-xs font-semibold text-slate-800">{c.label}</div>
                  <div className="text-[11px] text-slate-500">{c.detail}</div>
                </div>
              </div>
            ))}
            <div className={`rounded-md px-3 py-2 text-xs font-semibold ${data.can_close ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="checklist-verdict">
              {data.can_close ? "READY TO CLOSE ✓" : `CLOSE BLOCKED — ${data.blockers} failing check(s)`}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function YearEndCard({ onDone }) {
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPreview = async (y) => {
    try { const { data } = await api.get(`/finance/gl/year-end/preview?fiscal_year=${y}`); setPreview(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadPreview(year); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkClose = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/gl/periods/bulk-close", { fiscal_year: parseInt(year) });
      toast.success(`Closed ${data.closed.length} period(s)` + (data.skipped.length ? ` · ${data.skipped.length} blocked by checklist` : ""));
      loadPreview(year); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runClose = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/gl/year-end/close", { fiscal_year: parseInt(year) });
      toast.success(`Year ${year} closed via ${data.entry_no} — net ${formatCurrency(data.net_income)} to Retained Earnings`);
      loadPreview(year); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= new Date().getFullYear() - 5; y--) years.push(String(y));

  return (
    <div className="dc-card p-4 space-y-3" data-testid="year-end-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="overline flex items-center gap-1"><Flag size={12} /> Year-end close (retained earnings)</div>
          <div className="text-xs text-slate-500 mt-1">Zeroes all revenue & expense accounts into 3200 Retained Earnings. Requires every period of the year to be closed or locked.</div>
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-24 h-8" data-testid="yec-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {preview && (
        <div className="flex flex-wrap items-center gap-4 text-xs" data-testid="yec-preview">
          <span className="font-mono-alt tabular-nums">Revenue <b>{formatCurrency(preview.revenue_total)}</b></span>
          <span className="font-mono-alt tabular-nums">Expenses <b>{formatCurrency(preview.expense_total)}</b></span>
          <span className={`font-mono-alt tabular-nums font-bold ${preview.net_income >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            Net {formatCurrency(preview.net_income)}
          </span>
          {preview.already_closed ? (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600" data-testid="yec-already-closed">Already closed · {preview.existing_entry_no}</span>
          ) : preview.open_periods.length > 0 ? (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">{preview.open_periods.length} period(s) still open</span>
          ) : (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">Ready ✓</span>
          )}
        </div>
      )}
      <div className="flex gap-2">
        {preview?.open_periods?.length > 0 && (
          <Button variant="outline" size="sm" onClick={bulkClose} disabled={busy} data-testid="yec-bulk-close-btn">
            Close all {year} periods
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" className="btn-primary-blue" disabled={busy || !preview?.can_close} data-testid="yec-run-btn">
              Run year-end close {year}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Close fiscal year {year}?</AlertDialogTitle></AlertDialogHeader>
            <div className="text-sm text-slate-600">
              This posts a closing journal dated {year}-12-31 moving net {preview ? formatCurrency(preview.net_income) : ""} into Retained Earnings and zeroing {preview?.accounts_to_close || 0} P&L accounts. It can only run once per year.
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={runClose} data-testid="yec-confirm-btn">Close year {year}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export default function GLPeriodsTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [busy, setBusy] = useState(false);
  const [checklistPeriod, setChecklistPeriod] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/finance/gl/periods"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/finance/gl/periods/generate?year=${year}`);
      toast.success(r.created.length ? `Created ${r.created.length} periods for ${year}` : `All ${year} periods already exist`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const transition = async (period, action) => {
    try {
      await api.post(`/finance/gl/periods/${period}/transition`, { action });
      toast.success(`Period ${period} — ${action.replace("_", " ")} applied`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>;
  const items = data.items || [];
  const yearOptions = [];
  const nowY = new Date().getFullYear();
  for (let y = nowY - 4; y <= nowY + 1; y++) yearOptions.push(String(y));

  return (
    <div className="space-y-4" data-testid="gl-periods-tab">
      {isAdmin && <YearEndCard onDone={load} />}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          Lifecycle: <b>open → soft-closed → closed → locked</b>. Closing is gated by the checklist; locked periods can never be reopened.
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-24 h-8" data-testid="periods-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="btn-primary-blue" onClick={generate} disabled={busy} data-testid="periods-generate-btn">
              <ArrowsClockwise size={12} className={busy ? "animate-spin mr-1" : "mr-1"} /> Generate {year} periods
            </Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={CalendarBlank} title="No accounting periods"
          description="Generate a fiscal year to start controlling when journals may post." />
      ) : (
        <div className="dc-card responsive-table-wrap">
          <table className="data-table w-full min-w-[900px]">
            <thead>
              <tr>
                <th>Period</th><th>Label</th><th>Range</th><th>Status</th>
                <th>Closed / reopened</th><th className="w-64">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.period} data-testid={`period-row-${p.period}`}>
                  <td className="font-mono-alt text-xs tabular-nums">{p.period}</td>
                  <td className="font-semibold text-slate-900">{p.label}</td>
                  <td className="font-mono-alt text-xs tabular-nums">{formatDate(p.start_date)} → {formatDate(p.end_date)}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[p.status] || ""}`} data-testid={`period-status-${p.period}`}>
                      {p.status === "locked" ? <LockSimple size={10} weight="fill" /> : p.status === "open" ? <LockSimpleOpen size={10} /> : null}
                      {p.status.replace("_", "-")}
                    </span>
                  </td>
                  <td className="text-xs text-slate-500">
                    {p.status !== "open" && p.closed_by ? `${p.closed_by} · ${formatDate(p.closed_at)}` :
                      p.reopened_by ? `Reopened by ${p.reopened_by}` : "—"}
                  </td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1">
                      <Button variant="outline" size="sm" className="h-7 text-xs" title="Close checklist"
                        onClick={() => setChecklistPeriod(p.period)} data-testid={`period-checklist-${p.period}`}>
                        <ListChecks size={12} />
                      </Button>
                      {isAdmin && (ACTIONS_BY_STATUS[p.status] || []).map((a) => (
                        <Button key={a.action} variant="outline" size="sm" className="h-7 text-xs"
                          onClick={() => transition(p.period, a.action)}
                          data-testid={`period-${a.action}-${p.period}`}>
                          {a.label}
                        </Button>
                      ))}
                      {p.status === "locked" && <span className="text-[10px] uppercase tracking-wider text-slate-400">Immutable</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChecklistDialog period={checklistPeriod} onClose={() => setChecklistPeriod(null)} />
    </div>
  );
}
